import { Controller } from "@hotwired/stimulus"

// LRPO-5982 r6 — real chart titles for the pending overlay cards, keyed by
// the clicked tab's data-report-tab-key. Painted in _revealPendingOverlay so
// the overlay shows the tab's actual chart names instead of "Loading..."
// headers.
const REPORT_TAB_TITLES = {
  overview: ["Leads Per Day", "Leads Per Scanner (Top 15)"],
  lead_activity: ["Leads Per Day", "Leads Per Hour", "Hourly Heatmap", "Leads by Activation", "Leads Per Day by Activation", "Leads Per Hour by Activation"],
  rep_stats: ["Scanner Performance", "Coverage Gap Heatmap"],
  geography: ["Leads by US State", "Leads by Country", "Top States / Provinces", "Daily Spread — where leads came from each event day", "Lead Quality by Geography"],
  demographics: ["Business Type", "Volume"],
  reps_territories: ["Leads Routed by Rep", "Territory Roster", "Lead Distribution by Territory"],
  lead_quality: ["High-Value Lead Concentration by Hour", "Quality Score Distribution", "Quality Trend Over Days", "Average Quality by Scanner", "Lead Distribution by Company Type", "Top Buyer Profiles"],
  event_comparison: ["KPI Comparison", "Leads Per Day Overlay", "Long-Term Trend", "Event Score", "ROI Trend", "Timing Analysis", "People Analysis", "Audience & Quality"],
  templates: ["Reports"],
  roi: ["ROI Calculator"]
}

/**
 * Reports Hub tab indicator + pending state.
 *
 * The tab bar lives OUTSIDE the reports_tab_content Turbo Frame, so a
 * tab-link click only swaps the frame body — the active class on the
 * clicked link doesn't update without help. This controller toggles the
 * active state immediately on click so the indicator follows the user.
 *
 * Server-side render still authoritatively sets the active tab on full
 * page loads (bookmarked tab URLs) — this controller only handles the
 * client-side mid-session toggle.
 *
 * LRPO-5982 r2 — Turbo keeps the previous tab body until the server
 * answers, so Geography stayed on screen after Scanner Stats was already
 * highlighted. Show the first-load skeleton as an overlay on the frame
 * fetch, AFTER turbo:before-cache, so Back still restores the real tab
 * rather than a skeleton snapshot.
 *
 * LRPO-5982 r3 — turbo:render during that wait used to read the OLD
 * frame data-tab and light the previous tab again, so Overview and
 * Compare both looked selected. Honor the clicked tab until the new
 * frame lands. Overlay z-index is isolated so Compare's sticky sub-nav
 * cannot paint through it.
 */
export default class extends Controller {
  static targets = ["link"]

  connect() {
    this._clickedTab = null
    this._onPop = () => {
      this._clickedTab = null
      this._hidePendingOverlay()
      this.syncFromLocation()
    }
    this._onPopRender = () => this.syncFromLocation()
    this._onBeforeFetch = event => this._showPending(event)
    this._onFrameLoad = event => {
      this._hidePending(event)
      this._clickedTab = null
      this.syncFromLocation()
    }
    window.addEventListener("popstate", this._onPop)
    document.addEventListener("turbo:load", this._onPop)
    document.addEventListener("turbo:render", this._onPopRender)
    document.addEventListener("turbo:before-fetch-request", this._onBeforeFetch)
    document.addEventListener("turbo:frame-load", this._onFrameLoad)
    // LRPO-5982 r7 — first load: the skeleton inside the frame (and the
    // hidden overlay) render before any tab click, so paint their titles
    // from the server-set active tab; Overview gets its chart names.
    this._paintLoadingTitles(this._pending())
    this._paintLoadingTitles(this._frame())
  }

  disconnect() {
    window.removeEventListener("popstate", this._onPop)
    document.removeEventListener("turbo:load", this._onPop)
    document.removeEventListener("turbo:render", this._onPopRender)
    document.removeEventListener("turbo:before-fetch-request", this._onBeforeFetch)
    document.removeEventListener("turbo:frame-load", this._onFrameLoad)
    this._hidePendingOverlay()
    this._clickedTab = null
  }

  setActive(event) {
    const wasActive = event.currentTarget.classList.contains("active")
    this._clickedTab = event.currentTarget
    this._apply(this._clickedTab)
    // LRPO-5982 r5 — reveal the overlay on a real tab switch, before the
    // fetch starts, so the old tab body doesn't stay visible (QA flicker).
    if (wasActive) {
      // Same-tab refetch: the fetch still fires, so suppress the pending
      // overlay in _showPending instead of revealing it again.
      this._skipPending = true
    } else {
      this._revealPendingOverlay()
    }
  }

  // LRPO-5964 — swipe-back / browser Back restores the previous tab
  // body (Turbo + history) but left this bar on the tab you clicked
  // away from. Chrome Back can restore the frame body (Overview) while
  // the URL still points at the tab you left (Lead Quality), so the
  // frame's data-tab is the source of truth; the pathname is only a
  // fallback when the frame carries no key.
  //
  // LRPO-5982 r3 — while a click is in flight, the frame still holds
  // the previous tab. Do not let that undo the click highlight.
  syncFromLocation() {
    if (this._clickedTab) {
      this._apply(this._clickedTab)
      return
    }
    const frame = document.getElementById("reports_tab_content")
    const key = frame && frame.querySelector("[data-tab]")?.dataset.tab
    if (key) {
      const match = this.linkTargets.find(link => link.dataset.reportTabKey === key)
      if (match) return this._apply(match)
    }
    const path = window.location.pathname
    const match = this.linkTargets.find(link => {
      try {
        return new URL(link.href, window.location.origin).pathname === path
      } catch (e) {
        return false
      }
    })
    if (match) this._apply(match)
  }

  _apply(activeLink) {
    this.linkTargets.forEach(link => {
      const isActive = link === activeLink
      link.classList.toggle("active", isActive)
      link.setAttribute("aria-selected", isActive ? "true" : "false")
    })
  }

  _frame() {
    return document.getElementById("reports_tab_content")
  }

  _pending() {
    return document.getElementById("reports-tab-pending")
  }

  _isTabFrameEvent(event) {
    const frame = this._frame()
    if (!frame) return false
    if (event.target === frame) return true
    const headers = event.detail && event.detail.fetchOptions && event.detail.fetchOptions.headers
    if (!headers) return false
    return headers["Turbo-Frame"] === "reports_tab_content" || headers["turbo-frame"] === "reports_tab_content"
  }

  _revealPendingOverlay() {
    const pending = this._pending()
    if (!pending) return
    pending.classList.remove("d-none")
    pending.removeAttribute("hidden")
    pending.setAttribute("aria-busy", "true")
    // LRPO-5982 r6 — paint the active tab's real chart titles into the
    // overlay cards; hide any card beyond the tab's title list.
    this._paintLoadingTitles(pending)
    // LRPO-5982 r8 — the stage is a fixed 24rem scrollport, so extra
    // overlay cards sat below the fold. Grow the stage to the overlay's
    // full height so every painted card is visible.
    if (pending.parentElement) {
      pending.parentElement.style.minHeight = pending.scrollHeight + "px"
    }
  }

  // LRPO-5982 r7 — the clicked tab's key while a click is in flight, else
  // the server-set active tab's key (first load, no click yet).
  _activeTabKey() {
    if (this._clickedTab) return this._clickedTab.dataset.reportTabKey
    const active = this.linkTargets.find(link => link.classList.contains("active"))
    return active && active.dataset.reportTabKey
  }

  // LRPO-5982 r6 — paint the active tab's real chart titles into the
  // loading cards under root; hide any card beyond the tab's title list.
  _paintLoadingTitles(root) {
    if (!root) return
    const key = this._activeTabKey()
    const titles = REPORT_TAB_TITLES[key] || ["Chart", "Chart", "Chart", "Chart", "Chart"]
    root.querySelectorAll(".report-chart-card").forEach((card, i) => {
      const title = titles[i]
      if (title) {
        card.classList.remove("d-none")
        const header = card.querySelector("[data-loading-title]")
        if (header) header.textContent = title
      } else {
        card.classList.add("d-none")
      }
    })
  }

  _showPending(event) {
    if (!this._isTabFrameEvent(event)) return
    if (this._skipPending) {
      this._skipPending = false
      return
    }
    this._revealPendingOverlay()
  }

  _hidePending(event) {
    if (event && event.target && event.target !== this._frame()) return
    this._hidePendingOverlay()
  }

  _hidePendingOverlay() {
    const pending = this._pending()
    if (!pending) return
    pending.classList.add("d-none")
    pending.setAttribute("hidden", "")
    pending.removeAttribute("aria-busy")
    // LRPO-5982 r8 — undo the stage growth from _revealPendingOverlay.
    if (pending.parentElement) {
      pending.parentElement.style.minHeight = ""
    }
  }
}
